import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { IconMessages, IconStopwatch } from '@tabler/icons-react';
import { getTimeZones } from '@vvo/tzdb';

import {
  SelectDropdown,
  TextField,
  Typography,
} from '@zendesk/forethought-elements';
import { useDebouncedUpdateAbTestConfig } from './hooks';
import {
  formatAbTestDatetime,
  generateAbTestConfigRequestBody,
  getIsMinimumDurationMet,
} from './utils';
import { formatNumberWithCommas } from 'src/pages/workflow-builder/intent-workflows-table/utils';
import {
  useGetAbTestConfigByIdQuery,
  useListAvailableWinnerMetricsQuery,
} from 'src/services/agent-ab-test/agentAbTestApi';
import {
  EndCondition,
  MAX_WINNER_METRICS,
  WinnerMetric,
} from 'src/services/agent-ab-test/types';

const timeZoneOptions = getTimeZones({ includeUtc: true }).map(tz => ({
  label: tz.rawFormat,
  value: tz.name,
}));

const COMPOSITE_KEY_SEPARATOR = '::';

const toCompositeKey = (metric: WinnerMetric): string =>
  `${metric.metric_type}${COMPOSITE_KEY_SEPARATOR}${metric.metric_id}`;

const compositeKeysToWinnerMetrics = (keys: string[]): WinnerMetric[] =>
  keys.map(key => {
    const [metric_type, metric_id] = key.split(COMPOSITE_KEY_SEPARATOR);
    return { metric_id, metric_type };
  });

const WinnerTestEndSection = () => {
  const { t } = useTranslation('agent-ab-test');
  const { abTestId = '' } = useParams();

  const { data } = useGetAbTestConfigByIdQuery(abTestId, {
    skip: !abTestId,
  });

  const { data: winnerMetricsData } = useListAvailableWinnerMetricsQuery();

  const { end_condition, start_datetime, status, winner_metrics } = data || {};

  const isEditingDisabled = status !== 'draft';

  const { condition_type = 'fixed_duration', end_datetime = null } =
    end_condition || {};

  const { handleAbTestConfigUpdate } = useDebouncedUpdateAbTestConfig();

  const winnerMetricOptions = (winnerMetricsData?.predefined_metrics ?? []).map(
    metric => ({
      label: metric.display_name,
      value: toCompositeKey(metric),
    }),
  );

  const selectedWinnerMetricKeys = (winner_metrics ?? []).map(toCompositeKey);

  const getWinnerMetricsError = (): string | undefined => {
    if (!winner_metrics || winner_metrics.length === 0) {
      return undefined;
    }
    if (winner_metrics.length > MAX_WINNER_METRICS) {
      return t('winnerSection.maxMetricsError', { max: MAX_WINNER_METRICS });
    }
    return undefined;
  };

  const updateWinnerMetrics = (newKeys: string[]) => {
    if (!data) {
      return;
    }
    handleAbTestConfigUpdate(
      generateAbTestConfigRequestBody(data, {
        winner_metrics: compositeKeysToWinnerMetrics(newKeys),
      }),
      { immediate: true },
    );
  };

  // Convert ISO string to datetime-local format (YYYY-MM-DDTHH:mm)
  const toDatetimeLocalValue = (isoString: null | string) => {
    if (!isoString) {
      return '';
    }
    return isoString.slice(0, 16);
  };

  const conditionTypeToLabelMap: Record<string, string> = {
    conversations_count: t('winnerSection.conversationsCountLabel'),
    fixed_duration: t('winnerSection.fixedDurationLabel'),
  };
  const isEndConditionEmpty = end_condition === null;

  const defaultedEndCondition: EndCondition = {
    condition_type: 'fixed_duration',
    end_datetime: null,
    sample_count: null,
  };

  if (isEditingDisabled) {
    const winnerMetricDisplayNames = (winner_metrics ?? [])
      .map(wm => {
        const match = winnerMetricsData?.predefined_metrics.find(
          m => m.metric_id === wm.metric_id && m.metric_type === wm.metric_type,
        );
        return match?.display_name;
      })
      .filter(Boolean)
      .join(', ');

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <ReadOnlyField
          label={t('winnerSection.winnerMetricsLabel')}
          value={winnerMetricDisplayNames || t('winnerSection.noneDisplay')}
        />
        {start_datetime && (
          <ReadOnlyField
            label={t('winnerSection.testStartLabel')}
            value={formatAbTestDatetime(start_datetime, data?.timezone)}
          />
        )}
        {condition_type === 'fixed_duration' && end_datetime && (
          <ReadOnlyField
            label={t('winnerSection.testEndLabel')}
            value={formatAbTestDatetime(end_datetime, data?.timezone)}
          />
        )}
        {condition_type === 'sample_count' &&
          end_condition?.sample_count != null && (
            <ReadOnlyField
              label={t('winnerSection.numberOfConversationsLabel')}
              value={formatNumberWithCommas(end_condition.sample_count)}
            />
          )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        minWidth: 0,
      }}
    >
      <SelectDropdown
        chipsToShow={MAX_WINNER_METRICS}
        error={getWinnerMetricsError()}
        id='ab-test-winner-metric-selector'
        inputProps={{ readOnly: isEditingDisabled }}
        label={t('winnerSection.chooseMetricsLabel')}
        multiple
        onChange={e => {
          const newValue = e.target.value;
          if (!Array.isArray(newValue)) {
            return;
          }
          if (newValue.length > MAX_WINNER_METRICS) {
            return;
          }
          updateWinnerMetrics(newValue);
        }}
        onDeleteChip={(_e, chipValue) => {
          updateWinnerMetrics(
            selectedWinnerMetricKeys.filter(key => key !== chipValue),
          );
        }}
        options={winnerMetricOptions}
        value={selectedWinnerMetricKeys}
        wrapItems
      />
      <LabeledRow label={t('winnerSection.timeZoneLabel')}>
        <SelectDropdown
          id='ab-test-time-zone-selector'
          inputProps={{ readOnly: isEditingDisabled }}
          isMenuSearchable
          onChange={event => {
            if (!data) {
              return;
            }

            handleAbTestConfigUpdate(
              generateAbTestConfigRequestBody(data, {
                timezone: event.target.value,
              }),
              { immediate: true },
            );
          }}
          options={timeZoneOptions}
          value={data?.timezone ?? ''}
        />
      </LabeledRow>
      <LabeledRow label={t('winnerSection.endConditionLabel')}>
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
          }}
        >
          <ConditionItem
            icon={<IconStopwatch />}
            isSelected={condition_type === 'fixed_duration'}
            label={t('winnerSection.fixedDurationLabel')}
            onClick={() => {
              if (!data) {
                return;
              }
              const baseCondition = isEndConditionEmpty
                ? defaultedEndCondition
                : end_condition;

              const endCondition: EndCondition = {
                ...baseCondition,
                condition_type: 'fixed_duration',
              };

              handleAbTestConfigUpdate(
                generateAbTestConfigRequestBody(data, {
                  end_condition: {
                    ...endCondition,
                  },
                }),
                { immediate: true },
              );
            }}
            readOnly={isEditingDisabled}
          />
          <ConditionItem
            icon={<IconMessages />}
            isSelected={condition_type === 'sample_count'}
            label={conditionTypeToLabelMap['conversations_count']}
            onClick={() => {
              if (!data) {
                return;
              }
              const baseCondition = isEndConditionEmpty
                ? defaultedEndCondition
                : end_condition;

              const endCondition: EndCondition = {
                ...baseCondition,
                condition_type: 'sample_count',
              };

              handleAbTestConfigUpdate(
                generateAbTestConfigRequestBody(data, {
                  end_condition: {
                    ...endCondition,
                  },
                }),
                { immediate: true },
              );
            }}
            readOnly={isEditingDisabled}
          />
        </Box>
      </LabeledRow>
      <LabeledRow label={t('winnerSection.startDateLabel')}>
        <TextField
          aria-label={t('winnerSection.startDateLabel')}
          onChange={e => {
            if (!data) {
              return;
            }

            const value = e.target.value;
            const formattedDate = value ? `${value}:00.000` : null;

            handleAbTestConfigUpdate(
              generateAbTestConfigRequestBody(data, {
                start_datetime: formattedDate,
              }),
              { immediate: true },
            );
          }}
          readOnly={isEditingDisabled}
          type='datetime-local'
          value={toDatetimeLocalValue(start_datetime ?? null)}
        />
      </LabeledRow>
      {condition_type === 'fixed_duration' ? (
        <LabeledRow label={t('winnerSection.endDateLabel')}>
          <TextField
            aria-label={t('winnerSection.endDateLabel')}
            error={
              !end_datetime
                ? undefined
                : start_datetime &&
                    !getIsMinimumDurationMet(start_datetime, end_datetime)
                  ? t('winnerSection.minimumDurationError')
                  : undefined
            }
            inputProps={{
              min: toDatetimeLocalValue(start_datetime ?? null),
              readOnly: isEditingDisabled,
            }}
            onChange={e => {
              if (!data) {
                return;
              }

              const value = e.target.value;
              const formattedDate = value ? `${value}:00.000` : null;

              handleAbTestConfigUpdate(
                generateAbTestConfigRequestBody(data, {
                  end_condition: {
                    condition_type: 'fixed_duration',
                    end_datetime: formattedDate,
                    sample_count: null,
                  },
                }),
                { immediate: true },
              );
            }}
            readOnly={isEditingDisabled}
            shouldErrorMessageTakeUpSpace={false}
            type='datetime-local'
            value={toDatetimeLocalValue(end_datetime ?? null)}
          />
        </LabeledRow>
      ) : (
        <LabeledRow label={t('winnerSection.endAfterLabel')}>
          <TextField
            aria-label={t('winnerSection.endAfterAriaLabel')}
            endAdornment={
              <Typography variant='font14'>
                {t('trafficSplitTooltip.conversations')}
              </Typography>
            }
            error={
              Number(end_condition?.sample_count) > 0
                ? undefined
                : t('winnerSection.conversationsCountRequired')
            }
            onChange={e => {
              if (!data) {
                return;
              }

              const value = e.target.value;
              const numericValue = value === '' ? null : Number(value);

              handleAbTestConfigUpdate(
                generateAbTestConfigRequestBody(data, {
                  end_condition: {
                    condition_type: 'sample_count',
                    end_datetime: null,
                    sample_count: numericValue,
                  },
                }),
              );
            }}
            placeholder={t('winnerSection.enterNumberPlaceholder')}
            readOnly={isEditingDisabled}
            value={
              end_condition?.sample_count
                ? String(end_condition.sample_count)
                : ''
            }
          />
        </LabeledRow>
      )}
    </Box>
  );
};
export default WinnerTestEndSection;

const LABEL_WIDTH = 120;

// Small layout helper
const LabeledRow = ({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}) => (
  <Box
    sx={{
      alignItems: 'center',
      display: 'flex',
      gap: '16px',
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: `${LABEL_WIDTH}px`,
      }}
    >
      <Typography variant='font14'>{label}</Typography>
    </Box>
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  </Box>
);

interface ConditionItemProps {
  icon: React.ReactNode;
  isSelected?: boolean;
  label: string;
  onClick: () => void;
  readOnly?: boolean;
}

const ConditionItem = ({
  icon,
  isSelected = false,
  label,
  onClick,
  readOnly = false,
}: ConditionItemProps) => {
  const { palette } = useTheme();

  const getStyles = () => {
    if (isSelected) {
      return {
        backgroundColor: palette.colors.teal[100],
        border: `1px solid ${palette.colors.teal[500]}`,
        color: `${palette.colors.black} !important`,
        padding: '8px',
      };
    }
    return {
      backgroundColor: palette.colors.white,
      border: '1px solid #D1D5DB',
      color: `${palette.colors.grey[500]} !important`,
      padding: '8px',
    };
  };
  return (
    <Box
      {...getStyles()}
      component={'button'}
      disabled={readOnly}
      onClick={onClick}
      sx={[
        {
          alignItems: 'center',
          borderRadius: '6px',
          display: 'flex',
          flex: '1',
          gap: '4px',
          justifyContent: 'center',
        },
        readOnly
          ? { opacity: 0.5 }
          : {
              '&:hover': {
                border: `1px solid ${palette.colors.teal[500]}`,
              },
              '&:hover > *': {
                color: `${palette.colors.black} !important`,
              },
              cursor: 'pointer',
            },
      ]}
    >
      {icon}
      <Typography
        color={isSelected ? palette.colors.black : palette.colors.grey[500]}
        variant='font14'
      >
        {label}
      </Typography>
    </Box>
  );
};

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => {
  const { palette } = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography color={palette.colors.grey[600]} variant='font12'>
        {label}
      </Typography>
      <Typography variant='font14'>{value}</Typography>
    </Box>
  );
};
